// src/app/services/notification.service.ts
import { Injectable } from '@angular/core'
import { Router } from '@angular/router'
import { HttpClient } from '@angular/common/http'
import { BehaviorSubject, Observable, of } from 'rxjs'
import { catchError, map, tap } from 'rxjs/operators'
import { LocalServiceService } from 'src/app/services/local-service.service'
import { MessageUserService } from '../../../inbox/messaging/services/message-user.service'
import {
    markasReadResponse,
    MarkAsReadRequest,
    NotificationListResponse,
    NotificationParams,
    TopNotificationsResponse,
    UnreadCountResponse,
} from '../model/notification.model'

@Injectable({
    providedIn: 'root',
})
export class NotificationService {
    public _realTimeNotification = new BehaviorSubject<Notification[]>([])
    readonly realTimeNotification$ = this._realTimeNotification.asObservable()

    public _totalUnreadCount = new BehaviorSubject<number>(0)
    readonly totalUnreadCount$ = this._totalUnreadCount.asObservable()

    public _blink = new BehaviorSubject<boolean>(false)
    readonly blink$ = this._blink.asObservable()

    constructor(
        private router: Router,
        private http: HttpClient,
        private localService: LocalServiceService,
        private messagingService: MessageUserService,
    ) {}

    getNotifications(
        params: NotificationParams,
    ): Observable<NotificationListResponse> {
        const url = `/api/v2/platform/system-notifications?page=${params.page}&limit=${params.limit}&sort=${params.sort}&view=${params.view}`
        return this.http.get<NotificationListResponse>(url).pipe(
            tap((response: any) => {
                if (response.success) {
                    this._realTimeNotification.next(response.data)
                }
            }),
            catchError((err) => {
                console.error('Error fetching notifications')
                return of({ success: false, meta: { totalCount: 0 }, data: [] })
            }),
        )
    }

    getTopNotifications(): Observable<TopNotificationsResponse> {
        const url = `/api/v2/platform/system-notifications/recent`
        return this.http.get<TopNotificationsResponse>(url).pipe(
            tap((response: any) => {
                this._realTimeNotification.next(response.data)
            }),
            catchError((err) => {
                console.error('Error fetching top notifications')
                return of([])
            }),
        )
    }

    getUnreadCount(): Observable<UnreadCountResponse> {
        const url = `/api/v2/platform/system-notifications/unread-count`
        return this.http.get<UnreadCountResponse>(url).pipe(
            tap((response: any) => {
                const blink = response.data > 0 ? true : false
                this._blink.next(blink)
                this._totalUnreadCount.next(response.data)
            }),
            catchError((err) => {
                console.error('Error fetching top notifications')
                return of([])
            }),
        )
    }

    openNotification(
        type: string,
        patientId: string,
        notificationId?: string,
        permaLink?: string | null,
    ): void {
        if (type === 'task_note_tagged' || type === 'task_assignment') {
            const targetPath = this.getPermaLinkPath(permaLink)
            if (targetPath) {
                if (type === 'task_assignment') {
                    this.navigateToTask(targetPath)
                } else {
                    this.navigateWithReload(targetPath)
                }
                this.markASRead(patientId, notificationId, type).subscribe()
                return
            }
        }

        this.openMyConversation(patientId)
    }

    private navigateWithReload(targetPath: string): void {
        if (this.router.url === targetPath) {
            return
        }
        this.router
            .navigateByUrl('/', { skipLocationChange: true })
            .then(() => this.router.navigateByUrl(targetPath))
    }

    private navigateToTask(targetPath: string): void {
        window.location.assign(targetPath)
    }

    private getPermaLinkPath(permaLink?: string | null): string | null {
        if (!permaLink) {
            return null
        }

        if (permaLink.startsWith('/')) {
            return permaLink
        }

        if (permaLink.startsWith('http://') || permaLink.startsWith('https://')) {
            try {
                const url = new URL(permaLink)
                return `${url.pathname}${url.search}${url.hash}`
            } catch {
                return null
            }
        }

        return `/${permaLink}`
    }

    openMyConversation(id: string): void {
        const targetUrl = 'https://local-focus.patientbondcloud.com/focus/inbox'
        const currentUrl = window.location.href

        if (currentUrl !== targetUrl) {
            this.router.navigateByUrl('/focus/inbox').then(() => {
                this.selectConversation(id)
            })
        } else {
            this.selectConversation(id)
        }
    }

    markAllAsRead(): Observable<boolean> {
        const url = '/api/v2/platform/system-notifications/read-all'
        return this.http.put<{ success: boolean }>(url, {}).pipe(
            map((response) => response.success),
            tap(() => {
                this._realTimeNotification.next([])
                this._totalUnreadCount.next(0)
                this._blink.next(false)
            }),
            catchError((err) => {
                console.error('Error marking all notifications as read')
                return of(false)
            }),
        )
    }
    markASRead(
        patientId: string,
        notificationId?: string,
        type?: string,
    ): Observable<boolean> {
        const url = `/api/v2/platform/system-notifications/read`
        const body: MarkAsReadRequest = { patientId }
        if (notificationId) {
            body.notificationId = notificationId
        }
        if (type) {
            body.type = type
        }
        return this.http.put<markasReadResponse>(url, body).pipe(
            tap((respose)=>{
                this._totalUnreadCount.next(respose.data)
                if (respose.data === 0) {
                    this._blink.next(false)
                }
            }),
            map((response) => response.success),
            catchError((err) => {
                console.error('Error marking all notifications as read')
                return of(false)
            }),
        )
    }

    private selectConversation(id: string): void {
        this.localService.setInboxTabIndex('Messaging')
        this.messagingService.cleanUpForNotifications()
        this.messagingService.loadInitialPatients().subscribe(() => {
            this.messagingService.selectMessage(id)
            this.markASRead(id).subscribe()
        })
    }
}
